import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  ProgressBar,
  Badge,
} from "react-bootstrap";

import "bootstrap/dist/css/bootstrap.min.css";
import "../App.css";


const STATUS_LABELS = {
  queued: "Queued",
  in_progress: "Generating",
  completed: "Completed",
  failed: "Failed",
};

const STATUS_VARIANTS = {
  queued: "secondary",
  in_progress: "warning",
  completed: "success",
  failed: "danger",
};

function VideoGeneration({ openaiApiKey, setErrorMessage, activeTab, switchTab, videoPromptText, setVideoPromptText, videoReferenceImageUrl, setVideoReferenceImageUrl, missionData, workshopType, setVideoGenerationStarted }) {
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState("sora-2");
  const [duration, setDuration] = useState("4");

  const [videos, setVideos] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [expandedVideoIndex, setExpandedVideoIndex] = useState(null);
  const [clearConfirm, setClearConfirm] = useState(false);
  const [referenceImage, setReferenceImage] = useState(null);
  const [referenceImagePreview, setReferenceImagePreview] = useState(null);
  const [highlightRanges, setHighlightRanges] = useState(null);
  const MAX_VIDEO_TOKENS = 2;
  const [generatedCount, setGeneratedCount] = useState(0);
  const [confirmGo, setConfirmGo] = useState(false);

  const promptRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollingRef = useRef(null);
  const timerRef = useRef(null);
  const videosRef = useRef(videos);

  const STORAGE_KEY = "generated-videos";

  const saveVideosToStorage = (videoList) => {
    const toStore = videoList.map(({ videoUrl, ...rest }) => rest);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
    } catch (e) {
      console.error("Failed to save videos to localStorage:", e);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setReferenceImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setReferenceImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const clearReferenceImage = () => {
    setReferenceImage(null);
    setReferenceImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const resizeImageTo720p = (src) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 1280;
        canvas.height = 720;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, 1280, 720);
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.95);
      };
      img.src = src;
    });
  };

  const hasLoadedRef = useRef(false);

  useEffect(() => {
    videosRef.current = videos;
    if (hasLoadedRef.current) {
      saveVideosToStorage(videos);
    }
  }, [videos]);

  // Load persisted videos on mount and re-download completed ones
  useEffect(() => {
    hasLoadedRef.current = true;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed) || parsed.length === 0) return;
      const restored = parsed.map((v) => ({ ...v, videoUrl: null }));
      setVideos(restored);
      const usedTokens = restored.filter((v) => v.status === "completed" || v.status === "in_progress" || v.status === "queued").length;
      if (usedTokens > 0) {
        setGeneratedCount(usedTokens);
      }

      restored.forEach(async (v) => {
        if (v.status === "completed" && openaiApiKey) {
          try {
            const res = await fetch(
              `https://api.openai.com/v1/videos/${v.id}/content`,
              { headers: { Authorization: `Bearer ${openaiApiKey}` } }
            );
            if (res.ok) {
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              setVideos((prev) =>
                prev.map((pv) =>
                  pv.id === v.id ? { ...pv, videoUrl: url, status: "completed" } : pv
                )
              );
            }
          } catch (err) {
            console.error("Failed to re-download video:", v.id, err);
          }
        }
      });
    } catch (e) {
      console.error("Failed to load videos from localStorage:", e);
    }
  }, [openaiApiKey]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
      videosRef.current.forEach((v) => {
        if (v.videoUrl && v.videoUrl.startsWith("blob:")) {
          URL.revokeObjectURL(v.videoUrl);
        }
      });
    };
  }, []);

  // Focus prompt when switching to this tab
  useEffect(() => {
    if (activeTab === "video" && promptRef.current && videos.length === 0) {
      promptRef.current.focus();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "video" && videoPromptText) {
      const missionName = missionData?.name || 'the mission';
      if (videoReferenceImageUrl) {
        let prefix, fullPrompt, ranges;
        if (workshopType === 'film') {
          prefix = 'Create a cinematic trailer style video using the uploaded reference image as the exact visual design. Use smooth dynamic camera movement, including panning, tracking, and slow cinematic motion. Preserve the design exactly and maintain realistic lighting, scale, materials, and physics. No text, captions, logos, or additional design elements. Match this image prompt exactly: ';
          fullPrompt = prefix + videoPromptText;
          ranges = [
            { start: prefix.length, end: fullPrompt.length },
          ];
        } else {
          const beforeName = 'Create a cinematic, photorealistic video using the uploaded reference image as the exact design for the next gen ';
          const afterName = ' mission. Show the spacecraft in active motion with functioning systems. Use dynamic documentary style camera movement with smooth panning, tracking, and slow flyaround shots so the view feels alive and continuously changing. Maintain realistic lighting, scale, and physics. No text, captions, or logos. Match this image prompt exactly: ';
          prefix = beforeName + missionName + afterName;
          fullPrompt = prefix + videoPromptText;
          ranges = [
            { start: beforeName.length, end: beforeName.length + missionName.length },
            { start: prefix.length, end: fullPrompt.length },
          ];
        }
        setPrompt(fullPrompt);
        setHighlightRanges(ranges);
        setReferenceImagePreview(videoReferenceImageUrl);
        setReferenceImage(null);
        if (setVideoReferenceImageUrl) setVideoReferenceImageUrl(null);
      } else {
        setPrompt(videoPromptText);
        setHighlightRanges(null);
      }
      if (setVideoPromptText) setVideoPromptText('');
    }
  }, [activeTab, videoPromptText]);

  useEffect(() => {
    if (promptRef.current && prompt) {
      const resizePrompt = () => {
        if (promptRef.current) {
          promptRef.current.style.height = "auto";
          promptRef.current.style.height = Math.max(64, promptRef.current.scrollHeight) + "px";
        }
      };
      resizePrompt();
      requestAnimationFrame(resizePrompt);
    }
  }, [prompt]);

  const formatElapsedTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const startTimer = () => {
    setElapsedTime(0);
    const startTime = Date.now();
    timerRef.current = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startPolling = useCallback(
    (videoId) => {
      pollingRef.current = setInterval(async () => {
        try {
          const response = await fetch(
            `https://api.openai.com/v1/videos/${videoId}`,
            {
              headers: {
                Authorization: `Bearer ${openaiApiKey}`,
              },
            }
          );

          if (!response.ok) {
            const errData = await response.json().catch(() => ({}));
            throw new Error(
              errData.error?.message || `Status check failed (${response.status})`
            );
          }

          const data = await response.json();

          setVideos((prev) =>
            prev.map((v) =>
              v.id === videoId
                ? {
                    ...v,
                    status: data.status,
                    progress: data.progress || v.progress,
                  }
                : v
            )
          );

          if (data.status === "completed") {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            stopTimer();
            setIsGenerating(false);
            await downloadVideoContent(videoId);
          } else if (data.status === "failed") {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            stopTimer();
            setIsGenerating(false);
            if (setErrorMessage) {
              setErrorMessage(
                "Video generation failed. Try a different prompt or shorter duration."
              );
            }
          }
        } catch (err) {
          console.error("Polling error:", err);
        }
      }, 10000);
    },
    [openaiApiKey]
  );

  const downloadVideoContent = async (videoId) => {
    try {
      const response = await fetch(
        `https://api.openai.com/v1/videos/${videoId}/content`,
        {
          headers: {
            Authorization: `Bearer ${openaiApiKey}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to download video");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      setVideos((prev) => {
        const updated = prev.map((v) =>
          v.id === videoId ? { ...v, videoUrl: url, status: "completed" } : v
        );
        const idx = updated.findIndex((v) => v.id === videoId);
        if (idx !== -1) {
          setExpandedVideoIndex(idx);
        }
        return updated;
      });
    } catch (err) {
      console.error("Download error:", err);
      if (setErrorMessage) {
        setErrorMessage("Video generated but failed to download. Try refreshing.");
      }
    }
  };

  const handleGenerate = async (e) => {
    e?.preventDefault();
    if (!prompt.trim() || isGenerating || generatedCount >= MAX_VIDEO_TOKENS) return;

    if (!confirmGo) {
      setConfirmGo(true);
      return;
    }
    setConfirmGo(false);
    setGeneratedCount((c) => c + 1);
    if (setVideoGenerationStarted) setVideoGenerationStarted(true);

    if (!openaiApiKey) {
      if (setErrorMessage) {
        setErrorMessage("No API key configured. Please contact your instructor.");
      }
      return;
    }

    setIsGenerating(true);
    startTimer();

    try {
      const formData = new FormData();
      formData.append("model", model);
      formData.append("prompt", prompt.trim());
      formData.append("seconds", duration);
      formData.append("size", "1280x720");
      if (referenceImage) {
        const src = URL.createObjectURL(referenceImage);
        const resized = await resizeImageTo720p(src);
        URL.revokeObjectURL(src);
        formData.append("input_reference", resized, "reference.jpg");
      } else if (referenceImagePreview) {
        const resized = await resizeImageTo720p(referenceImagePreview);
        formData.append("input_reference", resized, "reference.jpg");
      }

      const response = await fetch("https://api.openai.com/v1/videos", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${openaiApiKey}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error?.message || `Request failed (${response.status})`
        );
      }

      const data = await response.json();

      const newVideo = {
        id: data.id,
        prompt: prompt.trim(),
        model: model,
        duration: duration,
        size: "1280x720",
        status: data.status || "queued",
        progress: 0,
        videoUrl: null,
        createdAt: new Date().toISOString(),
        hasReferenceImage: !!(referenceImage || referenceImagePreview),
        referenceImageUrl: referenceImagePreview || null,
      };

      setVideos((prev) => [...prev, newVideo]);
      clearReferenceImage();
      startPolling(data.id);
    } catch (err) {
      setIsGenerating(false);
      stopTimer();
      if (setErrorMessage) {
        setErrorMessage(err.message || "Failed to create video");
      }
    }
  };

  const handleDownloadToDevice = (video) => {
    if (!video.videoUrl) return;
    const a = document.createElement("a");
    a.href = video.videoUrl;
    a.download = `sora-video-${video.id.slice(-8)}.mp4`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleRemix = (video) => {
    setPrompt(video.prompt);
    setModel(video.model);
    setDuration(video.duration);

    if (promptRef.current) {
      promptRef.current.focus();
    }
    setTimeout(() => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: "smooth",
      });
    }, 100);
  };

  const handleDelete = (index) => {
    setVideos((prev) => {
      const video = prev[index];
      if (video.videoUrl && video.videoUrl.startsWith("blob:")) {
        URL.revokeObjectURL(video.videoUrl);
      }
      return prev.filter((_, i) => i !== index);
    });
    if (expandedVideoIndex === index) {
      setExpandedVideoIndex(null);
    } else if (expandedVideoIndex !== null && expandedVideoIndex > index) {
      setExpandedVideoIndex(expandedVideoIndex - 1);
    }
  };

  const handleClear = () => {
    if (clearConfirm) {
      if (pollingRef.current) clearInterval(pollingRef.current);
      stopTimer();
      setIsGenerating(false);
      videos.forEach((v) => {
        if (v.videoUrl && v.videoUrl.startsWith("blob:")) {
          URL.revokeObjectURL(v.videoUrl);
        }
      });
      setVideos([]);
      localStorage.removeItem(STORAGE_KEY);
      setExpandedVideoIndex(null);
      setClearConfirm(false);
      setConfirmGo(false);
    } else {
      setClearConfirm(true);
      setTimeout(() => setClearConfirm(false), 5000);
    }
  };

  // --- Render sections ---

  const renderGeneratingIndicator = () => {
    const activeVideo = videos.find(
      (v) => v.status === "queued" || v.status === "in_progress"
    );
    if (!activeVideo) return null;

    return (
      <div className="video-generating-indicator">
        <div className="video-generating-inner">
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
            <Spinner animation="border" size="sm" style={{ color: "#f05f40" }} />
            <span style={{ fontWeight: "bold", fontSize: "1rem" }}>
              Generating your video...
            </span>
            <Badge bg={STATUS_VARIANTS[activeVideo.status]}>
              {STATUS_LABELS[activeVideo.status]}
            </Badge>
          </div>
          <ProgressBar
            now={activeVideo.progress || (activeVideo.status === "queued" ? 5 : 50)}
            animated
            striped
            className="video-progress-bar"
            label={activeVideo.progress ? `${activeVideo.progress}%` : ""}
          />
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: "8px" }}>
            <span style={{ fontSize: "0.85rem", color: "#666" }}>
              {activeVideo.status === "queued"
                ? "Waiting in queue..."
                : "AI is rendering your video"}
            </span>
            <span style={{ fontSize: "0.85rem", color: "#999", fontFamily: "monospace" }}>
              {formatElapsedTime(elapsedTime)}
            </span>
          </div>
          <div style={{ fontSize: "0.75rem", color: "#aaa", marginTop: "4px" }}>
            "{activeVideo.prompt.length > 80
              ? activeVideo.prompt.slice(0, 80) + "..."
              : activeVideo.prompt}"
          </div>
        </div>
      </div>
    );
  };

  const renderVideoCard = (video, index) => {
    const isExpanded = expandedVideoIndex === index;
    const isCompleted = video.status === "completed" && video.videoUrl;
    const isFailed = video.status === "failed";
    const isProcessing =
      video.status === "queued" || video.status === "in_progress";

    return (
      <div key={video.id || index} className="video-gallery-item">
        {/* Prompt bubble */}
        <div className="video-prompt-bubble" style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: "#ddd", fontWeight: "bold", fontSize: "12px", fontStyle: "italic", marginBottom: "5px" }}>
              Prompt for Sora 2:
            </div>
            {video.prompt}
          </div>
          {video.referenceImageUrl && (
            <img
              src={video.referenceImageUrl}
              alt="Reference"
              style={{
                width: "100px",
                height: "100px",
                objectFit: "cover",
                borderRadius: "8px",
                border: "2px solid #f05f40",
                flexShrink: 0,
              }}
            />
          )}
        </div>

        {/* Video player or status */}
        <div className="video-player-container">
          {isCompleted ? (
            <div
              className={`video-player-wrapper ${isExpanded ? "video-expanded" : ""}`}
              onClick={() =>
                setExpandedVideoIndex(isExpanded ? null : index)
              }
            >
              <video
                controls
                playsInline
                preload="metadata"
                className="video-player"
                onClick={(e) => e.stopPropagation()}
              >
                <source src={video.videoUrl} type="video/mp4" />
                Your browser does not support video playback.
              </video>
            </div>
          ) : isProcessing ? (
            <div className="video-placeholder-processing">
              <Spinner animation="border" style={{ color: "#f05f40" }} />
              <div style={{ marginTop: "10px", fontWeight: "bold" }}>
                {STATUS_LABELS[video.status]}
              </div>
              {video.progress > 0 && (
                <ProgressBar
                  now={video.progress}
                  animated
                  striped
                  className="video-progress-bar"
                  style={{ width: "80%", marginTop: "10px" }}
                  label={`${video.progress}%`}
                />
              )}
            </div>
          ) : isFailed ? (
            <div className="video-placeholder-failed">
              <div style={{ fontSize: "2rem" }}>!</div>
              <div style={{ marginTop: "5px", fontWeight: "bold", color: "#dc3545" }}>
                Generation Failed
              </div>
            </div>
          ) : null}
        </div>

        {/* Metadata */}
        <div className="video-meta-row">
          <Badge bg={STATUS_VARIANTS[video.status]} className="video-status-badge">
            {STATUS_LABELS[video.status]}
          </Badge>
          <span className="video-meta-info">
            {video.model === "sora-2-pro" ? "Sora 2 Pro" : "Sora 2"} &middot;{" "}
            {video.duration}s &middot;{" "}
            {video.size}
          </span>
        </div>

        {/* Action buttons */}
        <div className="video-actions">
          {isCompleted && (
            <>
              <Button
                variant="outline-primary"
                size="sm"
                className="btn-video-action"
                onClick={() =>
                  setExpandedVideoIndex(isExpanded ? null : index)
                }
              >
                {isExpanded ? "Collapse" : "Expand"}
              </Button>
              <Button
                variant="outline-success"
                size="sm"
                className="btn-video-action"
                onClick={() => handleDownloadToDevice(video)}
              >
                Download
              </Button>
            </>
          )}
          <Button
            variant="outline-secondary"
            size="sm"
            className="btn-video-action"
            onClick={() => handleRemix(video)}
          >
            Remix
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            className="btn-video-action"
            onClick={() => handleDelete(index)}
          >
            Delete
          </Button>
        </div>
      </div>
    );
  };

  const renderGallery = () => {
    if (videos.length === 0) return null;

    return (
      <div className="video-gallery">
        {videos.map((video, index) => renderVideoCard(video, index))}
      </div>
    );
  };


  return (
    <Container className="mt-1 mb-0">
      <Row className="mb-0">
        <Col>
          <Card style={{ borderColor: "#c8decf" }}>
            <Card.Header
              as="h4"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "#dff0e6",
                borderColor: "#c8decf",
              }}
            >
              Generate Videos with Sora 2
              <span style={{ display: "flex", alignItems: "center", gap: "10px", marginLeft: "auto" }}>
                <span style={{ fontSize: "0.85rem", fontWeight: "500", color: "#555" }}>
                  You have {MAX_VIDEO_TOKENS - generatedCount} attempt{MAX_VIDEO_TOKENS - generatedCount !== 1 ? "s" : ""} remaining
                </span>
                {videos.length > 0 && (
                  <Button
                    size="sm"
                    className="btn-clear-video"
                    style={{
                      fontSize: "0.65rem",
                      padding: "0.15rem 0.4rem",
                    }}
                    variant={clearConfirm ? "danger" : "outline-secondary"}
                    onClick={handleClear}
                  >
                    {clearConfirm ? "Confirm Clear" : "Clear Videos"}
                  </Button>
                )}
              </span>
            </Card.Header>
            <Card.Body style={{ backgroundColor: "#f8fcf9" }}>
              {/* Instruction box */}
              <div
                style={{
                  backgroundColor: "white",
                  border: "2px solid #f05f40",
                  borderRadius: "8px",
                  padding: "10px 15px 15px 15px",
                  margin: "10px 0",
                  boxShadow: "0 0 10px rgba(240, 95, 64, 0.3)",
                }}
              >
                <h3
                  style={{
                    color: "#999",
                    fontWeight: "bold",
                    margin: "0 0 5px 0",
                    fontSize: "12px",
                    fontStyle: "italic",
                  }}
                >
                  Instructions:
                </h3>
                <h4
                  style={{
                    color: "#f05f40ff",
                    fontWeight: "bold",
                    margin: "0 0 5px 0",
                  }}
                >
                  Create AI-generated videos from text descriptions using
                  OpenAI's Sora 2.
                </h4>
                <p
                  style={{
                    color: "#f05f40ff",
                    fontWeight: "bold",
                    margin: "0",
                  }}
                >
                  Describe the scene, camera movement, lighting, and mood. Be
                  specific for best results.
                </p>
                <p
                  style={{
                    color: "#555",
                    fontWeight: "bold",
                    margin: "8px 0 0 0",
                  }}
                >
                  You have {MAX_VIDEO_TOKENS} video generation attempts, so make sure your prompt is fully updated before selecting Go.
                </p>
              </div>

              {/* Generating indicator */}
              {isGenerating && renderGeneratingIndicator()}

              {/* Video gallery */}
              {renderGallery()}

              {/* Separator */}
              {videos.length > 0 && <hr style={{ marginTop: "30px" }} />}

              {/* Prompt input */}
              <Form onSubmit={handleGenerate}>
                <div style={{ position: "relative" }}>
                  <div
                    className="form-control orange-highlight"
                    style={{ padding: 0, overflow: "visible", position: "relative", height: "auto" }}
                    onClick={() => promptRef.current?.focus()}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: "8px",
                        left: "50px",
                        color: "#999",
                        fontWeight: "bold",
                        fontSize: "12px",
                        fontStyle: "italic",
                        zIndex: 10,
                        pointerEvents: "none",
                      }}
                    >
                      Prompt for {model === "sora-2-pro" ? "Sora 2 Pro" : "Sora 2"}:
                    </div>
                    <div style={{ position: "relative" }}>
                      {highlightRanges && highlightRanges.length > 0 && (
                        <div
                          aria-hidden="true"
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            padding: "0.375rem 0.75rem",
                            fontSize: "inherit",
                            fontFamily: "inherit",
                            lineHeight: "1.5",
                            whiteSpace: "pre-wrap",
                            wordWrap: "break-word",
                            color: "transparent",
                            pointerEvents: "none",
                            overflow: "hidden",
                          }}
                        >
                          {(() => {
                            const parts = [];
                            let lastEnd = 0;
                            for (const range of highlightRanges) {
                              if (range.start > lastEnd) {
                                parts.push(prompt.slice(lastEnd, range.start));
                              }
                              parts.push(
                                <mark key={range.start} style={{ background: "#f4e4bc", color: "transparent", borderRadius: "2px" }}>
                                  {prompt.slice(range.start, range.end)}
                                </mark>
                              );
                              lastEnd = range.end;
                            }
                            if (lastEnd < prompt.length) {
                              parts.push(prompt.slice(lastEnd));
                            }
                            return parts;
                          })()}
                        </div>
                      )}
                      <textarea
                        ref={promptRef}
                        rows={3}
                        value={prompt}
                        onChange={(e) => {
                          setPrompt(e.target.value);
                          setHighlightRanges(null);
                          if (confirmGo) setConfirmGo(false);
                        }}
                        placeholder="A serene underwater scene with bioluminescent jellyfish floating through a coral reef, cinematic lighting, slow camera dolly..."
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleGenerate(e);
                          }
                        }}
                        onInput={(e) => {
                          const textarea = e.target;
                          textarea.style.height = "auto";
                          textarea.style.height =
                            Math.max(64, textarea.scrollHeight) + "px";
                        }}
                        style={{
                          position: "relative",
                          minHeight: "4em",
                          width: "100%",
                          resize: "none",
                          overflow: "hidden",
                          border: "none",
                          boxShadow: "none",
                          outline: "none",
                          padding: "0.375rem 0.75rem",
                          fontSize: "inherit",
                          fontFamily: "inherit",
                          lineHeight: "1.5",
                          background: "transparent",
                        }}
                        disabled={isGenerating}
                      />
                    </div>
                    {referenceImagePreview && (
                      <div style={{ padding: "0 10px 10px 10px" }}>
                        <div style={{ position: "relative", display: "inline-block" }}>
                          <img
                            src={referenceImagePreview}
                            alt="Reference"
                            style={{
                              height: "80px",
                              width: "80px",
                              objectFit: "cover",
                              borderRadius: "8px",
                              border: "2px solid #f05f40",
                            }}
                          />
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); clearReferenceImage(); }}
                            style={{
                              position: "absolute",
                              top: "-8px",
                              right: "-8px",
                              background: "#f05f40",
                              color: "white",
                              border: "none",
                              borderRadius: "50%",
                              width: "18px",
                              height: "18px",
                              fontSize: "12px",
                              lineHeight: "1",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            &times;
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <Button
                    type="submit"
                    variant={confirmGo ? "success" : "primary"}
                    className="btn-video-generate"
                    disabled={isGenerating || !prompt.trim() || generatedCount >= MAX_VIDEO_TOKENS}
                    style={
                      generatedCount >= MAX_VIDEO_TOKENS
                        ? { backgroundColor: "#ccc", borderColor: "#ccc", color: "#888", cursor: "not-allowed" }
                        : confirmGo
                        ? { backgroundColor: "#3bbf6b", borderColor: "#3bbf6b" }
                        : {}
                    }
                  >
                    {isGenerating ? (
                      <Spinner animation="border" size="sm" />
                    ) : generatedCount >= MAX_VIDEO_TOKENS ? (
                      "Go"
                    ) : confirmGo ? (
                      <span style={{ fontSize: "0.55rem" }}>Confirm Go</span>
                    ) : (
                      "Go"
                    )}
                  </Button>
                </div>

                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/jpeg,image/png,image/webp"
                  style={{ display: "none" }}
                  onChange={handleImageUpload}
                />
                <div style={{ display: "flex", flexDirection: "row", flexWrap: "wrap", alignItems: "center", justifyContent: "flex-end", gap: "12px 16px", marginTop: "8px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "4px", marginRight: "auto" }}>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isGenerating}
                      className="btn-video-action"
                      style={{ fontSize: "11px", padding: "2px 10px", borderRadius: "20px" }}
                    >
                      {referenceImagePreview ? "Change Image" : "Upload Image"}
                    </Button>
                    {referenceImagePreview && (
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={clearReferenceImage}
                        disabled={isGenerating}
                        className="btn-video-action"
                        style={{ fontSize: "11px", padding: "2px 10px", borderRadius: "20px" }}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                  <div className="video-model-selector" style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <span style={{ fontSize: "12px", fontWeight: "bold", color: "#555", marginRight: "4px" }}>Model:</span>
                    {[
                      { value: "sora-2", label: "Sora 2 (Fast)" },
                      { value: "sora-2-pro", label: "Sora 2 Pro (Quality)" },
                    ].map((opt) => (
                      <Button
                        key={opt.value}
                        size="sm"
                        className="btn-video-setting"
                        variant={model === opt.value ? "primary" : "outline-secondary"}
                        onClick={() => setModel(opt.value)}
                        disabled={isGenerating}
                        style={{ fontSize: "11px", padding: "2px 10px", borderRadius: "20px" }}
                      >
                        {opt.label}
                      </Button>
                    ))}
                  </div>
                  <div className="video-duration-selector" style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <span style={{ fontSize: "12px", fontWeight: "bold", color: "#555", marginRight: "4px" }}>Duration:</span>
                    {["4", "8", "12"].map((sec) => (
                      <Button
                        key={sec}
                        size="sm"
                        className="btn-video-setting"
                        variant={duration === sec ? "primary" : "outline-secondary"}
                        onClick={() => setDuration(sec)}
                        disabled={isGenerating}
                        style={{ fontSize: "11px", padding: "2px 10px", borderRadius: "20px" }}
                      >
                        {sec}s
                      </Button>
                    ))}
                  </div>
                </div>


              </Form>

              {videos.length > 0 && (
                <>
                  <hr style={{ marginTop: "20px" }} />
                  <div style={{
                    backgroundColor: "white",
                    fontSize: "large",
                    border: "2px solid #f05f40",
                    borderRadius: "15px",
                    padding: "10px 15px 15px 15px",
                    margin: "15px 0 10px 0",
                    boxShadow: "0 0 15px rgba(240, 95, 64, 0.6)",
                    width: window.innerWidth < 992 ? "100%" : "50%"
                  }}>
                    <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>
                      Instructions:
                    </h3>
                    <span style={{ margin: "0", lineHeight: "2.2" }}>
                      {workshopType === "mission"
                        ? "Generate more elements for your mission using "
                        : "Generate more production elements for your film using "}
                      <Button
                        className="btn-nav-link"
                        onClick={() => { if (setVideoGenerationStarted) setVideoGenerationStarted(true); switchTab('gpt'); }}
                        style={{
                          margin: "0 5px",
                          padding: "4px 12px",
                          fontSize: "14px",
                          display: "inline",
                          whiteSpace: "nowrap",
                          backgroundColor: "white",
                          borderColor: "#f05f40",
                          color: "#f05f40",
                          verticalAlign: "middle",
                        }}
                      >
                        Text
                      </Button>
                    </span>
                  </div>
                  <hr style={{ marginTop: "15px" }} />
                </>
              )}
            </Card.Body>

            <Card.Footer
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                padding: "0.4rem 0.75rem",
                backgroundColor: "#dff0e6",
              }}
            >
              <div style={{ display: "flex", gap: "0.3rem" }}>
                {videos.length > 0 && (
                  <Button
                    size="sm"
                    className="btn-clear-video"
                    style={{
                      fontSize: "0.65rem",
                      padding: "0.15rem 0.4rem",
                    }}
                    variant={clearConfirm ? "danger" : "outline-secondary"}
                    onClick={handleClear}
                  >
                    {clearConfirm ? "Confirm Clear" : "Clear Videos"}
                  </Button>
                )}
              </div>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default VideoGeneration;
